import {ChevronLeftIcon, ChevronRightIcon, EllipsisIcon} from "lucide-react";
import Icon from "./icon";
import cn from "@/utils/cn";

type PaginationProps =
{
    page: number;
    pageCount: number;
    setPage: (page: number) => void;
    buttonCount?: number;
    variant?: "default" | "glued";
};

export default function TablePagination({page, pageCount, setPage, buttonCount = 7, variant = "default"}: PaginationProps)
{
    let offset = 1;
    offset = Math.max(Math.round(page - buttonCount / 2), 1);
    offset = Math.min(offset, pageCount - buttonCount + 1);
    offset = Math.max(offset, 1);

    return (
        <div className={cn("flex -space-x-px justify-center text-neutral-300", variant === "default" && "p-2.5")} role="group">
            <button disabled={page === 1} onClick={() => setPage(page - 1)} className={cn(
                "text-center px-1.5 py-1 hover:text-neutral-100",
                page !== 1 && "hover:bg-neutral-800 cursor-pointer",
                variant === "default" && "border rounded-l",
                variant === "glued" && "border-r",
            )}>
                <Icon component={ChevronLeftIcon} />
            </button>

            <button disabled={page === 1} onClick={() => setPage(1)} className={cn(
                "text-center px-2.5 py-1 hover:text-neutral-100",
                page === 1 ? "bg-neutral-900" : "cursor-pointer hover:bg-neutral-800",
                variant === "default" && "border",
                variant === "glued" && "border-x border-b",
            )}>
                1
            </button>

            {(offset > 1) &&
                <button disabled className={cn(
                    "text-center px-2.5 py-1 text-neutral-500",
                    variant === "default" && "border",
                    variant === "glued" && "border-x border-b",
                )}>
                    <Icon component={EllipsisIcon} />
                </button>
            }

            {Array.from({length: buttonCount - 2}).map((_, i) => (i + 1 < pageCount) && 
                <button key={i} onClick={() => setPage(offset + i + 1)} className={cn(
                    "text-center px-2.5 py-1 hover:text-neutral-100",
                    page === offset + i + 1 ? "bg-neutral-900" : "cursor-pointer hover:bg-neutral-800",
                    variant === "default" && "border",
                    variant === "glued" && "border-x border-b",
                )}>
                    {offset + i + 1}
                </button>
            )}

            {((offset + buttonCount) < pageCount + 1) &&
                <button disabled className={cn(
                    "text-center px-2.5 py-1 text-neutral-500",
                    variant === "default" && "border",
                    variant === "glued" && "border-x border-b",
                )}>
                    <Icon component={EllipsisIcon} />
                </button>
            }

            {(buttonCount - 1 < pageCount) &&
                <button disabled={page >= pageCount} onClick={() => setPage(pageCount)} className={cn(
                    "text-center px-2.5 py-1 hover:text-neutral-100",
                    page >= pageCount ? "bg-neutral-900" : "cursor-pointer hover:bg-neutral-800",
                    variant === "default" && "border",
                    variant === "glued" && "border-x border-b",
                )}>
                    {pageCount}
                </button>
            }

            <button disabled={page >= pageCount} onClick={() => setPage(page + 1)} className={cn(
                "text-center px-1.5 py-1 hover:text-neutral-100",
                page < pageCount && "hover:bg-neutral-800 cursor-pointer",
                variant === "default" && "border rounded-r",
                variant === "glued" && "border-l",
            )}>
                <Icon component={ChevronRightIcon} />
            </button>
        </div>
    );
}
